// src/hooks/usePWAInstall.js
import { useState, useEffect } from 'react';

export const usePWAInstall = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [canInstall, setCanInstall] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Verifica se già installata
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    const isInWebAppiOS = window.navigator.standalone === true;
    setIsInstalled(isStandalone || isInWebAppiOS);

    // Listener per evento beforeinstallprompt
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setCanInstall(true);
    };

    // Listener per evento appinstalled
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setCanInstall(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const installApp = async () => {
    if (!deferredPrompt) {
      return false;
    }

    try {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
            
      if (outcome === 'accepted') {
        setCanInstall(false);
        setDeferredPrompt(null);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Errore installazione PWA:', error);
      return false;
    }
  };

  const checkInstallability = () => {
    // Verifica se l'app può essere installata
    return canInstall && !isInstalled && deferredPrompt !== null;
  };

  const getInstallInstructions = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    
    if (/iphone|ipad|ipod/.test(userAgent) && /safari/.test(userAgent) && !/crios/.test(userAgent)) {
      return {
        platform: 'iOS Safari',
        steps: [
          'Tocca l\'icona di condivisione',
          'Scorri e tocca "Aggiungi alla schermata Home"',
          'Tocca "Aggiungi" per confermare'
        ]
      };
    }
    
    if (/android/.test(userAgent) && /chrome/.test(userAgent)) {
      return {
        platform: 'Android Chrome',
        steps: [
          'Tocca il menu (⋮) in alto a destra',
          'Tocca "Installa app" o "Aggiungi alla schermata Home"',
          'Tocca "Installa" per confermare'
        ]
      };
    }
    
    if (/chrome/.test(userAgent)) {
      return {
        platform: 'Chrome Desktop',
        steps: [
          'Clicca l\'icona di installazione nella barra degli indirizzi',
          'Oppure vai al menu Chrome > Altri strumenti > Crea scorciatoia',
          'Seleziona "Apri come finestra" per un\'esperienza app-like'
        ]
      };
    }
    
    return {
      platform: 'Browser generico',
      steps: [
        'Cerca l\'opzione "Installa" o "Aggiungi alla schermata Home" nel menu del browser',
        'Segui le istruzioni specifiche del tuo browser'
      ]
    };
  };

  return {
    canInstall: checkInstallability(),
    isInstalled,
    installApp,
    getInstallInstructions
  };
};